/**
 * Сухой прогон связки Polymarket × Cloudbet.
 *
 * Смотрит кадр ленты 4.0 (`/dry-api`) и говорит, какую вилку смотрел бы. Ордера здесь нет:
 * ни подписи, ни ключа, ни запроса на ставку. Cloudbet из фида тоже не ставим —
 * лимит фида не равен сумме, которую контора примет.
 */

import { Decimal } from 'decimal.js';

import { polyEventSlug } from '../poly/coupon';
import { slugOf } from './polyBook';

/** Рафик и Удмурт. Чужой id или чужой username страницу не открывает. */
const PILOT_IDS = new Set(['7206873220', '8030661704']);
const PILOT_NAMES = new Set(['rafic_traffic', 'udmurte']);

/** Единственный источник карточек и кандидатов бота. Ошибка feed4 не включает чужую ленту. */
export const DRY_TAPE = '/dry-api/v1/signals';
export const DRY_TAPES = [DRY_TAPE] as const;

/** Полная FORQ с `poly.js`. С 0.4.135 это и есть публичный ZIP. */
export const DRY_FULL_EXT = '/forq-extension.zip';

/** Склейка кадров: один id — одна вилка, первый источник главнее. */
export function mergeTapes<T extends { id: string }>(tapes: ReadonlyArray<readonly T[] | null>): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const forks of tapes) {
    for (const fork of forks ?? []) {
      if (seen.has(fork.id)) continue;
      seen.add(fork.id);
      out.push(fork);
    }
  }
  return out;
}

/** Мёртвая своя лента не оставляет чужой кадр `/app` или `/tests`. */
export function keepTape<T>(ok: boolean, rows: Map<string, T>): Map<string, T> {
  return ok ? rows : new Map();
}

/** Возраст ленты на экране: лайв 4 мин, прематч 10 мин. Живые книги — другие часы. */
export const LIVE_TAPE_SEC = 240;
export const PRE_TAPE_SEC = 600;
/** @deprecated то же, что LIVE_TAPE_SEC. Для старых тестов. */
export const FRESH_SEC = LIVE_TAPE_SEC;

export function tapeAgeLimit(line?: string | null): number {
  return line === 'prematch' ? PRE_TAPE_SEC : LIVE_TAPE_SEC;
}

const DOORS = new Set([
  'нет ссылки на ногу биржи',
  'нет ссылки на ногу конторы',
  'в ссылке биржи нет рынка исхода',
  'токен исхода с биржи не открылся',
]);

/** Брак, который должен быть на экране и в журнале, не в тишине. */
export function doorNote(paper: Paper): { step: string; detail: string; tone: 'no' } | null {
  if (!paper.look || !DOORS.has(paper.why)) return null;
  const detail = `${paper.match} · ${paper.why}`.slice(0, 160);
  return { step: 'дверь', detail, tone: 'no' };
}

export const PAIR = ['cloudbet', 'polymarket'] as const;

export interface PaperLeg {
  s: string;
  side: string;
  odds: string;
  url: string | null;
  betUrl?: string;
  participants?: string[];
  lay?: boolean;
  asset?: string;
  unit?: 'share' | 'money';
  name?: string | null;
  still?: number | null;
  stillSure?: boolean;
  /** Билет ноги для водителя: `eid~market~outcome[~params]`. */
  sel?: string;
}

export interface PaperFork {
  id: string;
  roi?: string;
  age?: number;
  line?: string;
  sport?: string;
  market?: string;
  league?: string;
  legs?: PaperLeg[];
}

export interface Paper {
  id: string;
  match: string;
  sport: string;
  market: string;
  roi: string;
  age: number | null;
  books: string;
  polyOdds: string;
  cloudOdds: string;
  polyUrl: string | null;
  cloudUrl: string | null;
  look: boolean;
  why: string;
}

export function isPilot(me: { id: string | null; username: string | null }): boolean {
  if (me.id !== null && PILOT_IDS.has(me.id)) return true;
  const name = (me.username ?? '').replace(/^@/, '').trim().toLowerCase();
  return PILOT_NAMES.has(name);
}

function legOf(fork: PaperFork, code: (typeof PAIR)[number]): PaperLeg | null {
  const found = (fork.legs ?? []).filter((leg) => leg.s === code);
  const one = found[0];
  return found.length === 1 && one !== undefined ? one : null;
}

function oddsOk(raw: string): boolean {
  try {
    return new Decimal(raw).gt(1);
  } catch {
    return false;
  }
}

function named(leg: PaperLeg | null): string {
  const people = (leg?.participants ?? []).map((item) => item.trim()).filter(Boolean);
  return people.length > 0 ? people.join(' — ') : '';
}

function textOf(fork: PaperFork, poly: PaperLeg, cloud: PaperLeg): string {
  const bits = [fork.league ?? '', fork.market ?? '', poly.name ?? '', cloud.name ?? ''];
  for (const leg of [poly, cloud]) {
    for (const name of leg.participants ?? []) bits.push(name);
  }
  return bits.join(' ').toLowerCase();
}

function simulated(fork: PaperFork, poly: PaperLeg, cloud: PaperLeg): boolean {
  const text = textOf(fork, poly, cloud);
  if (text.includes('simulated-reality') || text.includes('(srl)')) return true;
  return text.split(/[\s/]+/).some((part) => part.endsWith('-srl'));
}

function quarter(fork: PaperFork, poly: PaperLeg, cloud: PaperLeg): boolean {
  return /(?:^|[^\d])\d*\.(?:25|75)(?:$|[^\d])/.test(textOf(fork, poly, cloud));
}

function booksOf(poly: PaperLeg, cloud: PaperLeg): string {
  return `Polymarket ${poly.odds} · Cloudbet ${cloud.odds}`;
}

function linkOf(leg: PaperLeg | null): string | null {
  if (leg === null) return null;
  const link = leg.betUrl || leg.url;
  return link ? link : null;
}

function row(fork: PaperFork, poly: PaperLeg | null, cloud: PaperLeg | null, look: boolean, why: string): Paper {
  const match = named(poly) || named(cloud) || 'матч без имени';
  return {
    id: fork.id,
    match,
    sport: fork.sport ?? '',
    market: fork.market ?? '',
    roi: fork.roi ?? '',
    age: typeof fork.age === 'number' ? fork.age : null,
    books: poly && cloud ? booksOf(poly, cloud) : '',
    polyOdds: poly?.odds ?? '',
    cloudOdds: cloud?.odds ?? '',
    polyUrl: linkOf(poly),
    cloudUrl: linkOf(cloud),
    look,
    why,
  };
}

/** Токен, прочитанный со страницы по ссылке ноги. Провод ленты его не несёт. */
export function arm(fork: PaperFork, token: string | null): PaperFork {
  if (token === null || token.trim() === '') return fork;
  const legs = fork.legs ?? [];
  return {
    ...fork,
    legs: legs.map((leg) =>
      leg.s === 'polymarket' && (leg.asset === undefined || leg.asset.trim() === '')
        ? { ...leg, asset: token }
        : leg,
    ),
  };
}

/** Одна вилка. `look` — на экране. Жать или нет решает охота, не судья. */
export function judge(fork: PaperFork): Paper {
  const legs = fork.legs ?? [];
  const poly = legOf(fork, 'polymarket');
  const cloud = legOf(fork, 'cloudbet');
  if (legs.length !== 2 || poly === null || cloud === null) {
    return row(fork, poly, cloud, false, 'не связка Polymarket и Cloudbet');
  }
  if (!oddsOk(poly.odds) || !oddsOk(cloud.odds)) return row(fork, poly, cloud, false, 'кэф не читается');
  if (poly.lay || cloud.lay) return row(fork, poly, cloud, true, 'нога против: на бирже это другой токен');
  if (poly.unit === 'money') return row(fork, poly, cloud, true, 'биржа пришла как контора');
  if (simulated(fork, poly, cloud)) return row(fork, poly, cloud, true, 'симуляция, не матч');
  if (quarter(fork, poly, cloud)) return row(fork, poly, cloud, true, 'азиатская четверть: возможен возврат');
  const ageLimit = tapeAgeLimit(fork.line);
  if (typeof fork.age === 'number' && fork.age > ageLimit) {
    return row(fork, poly, cloud, true, `старше ${ageLimit} с`);
  }
  const polyLink = poly.betUrl || poly.url;
  const cloudLink = cloud.betUrl || cloud.url;
  // Бот ставит на контору через sel (билет), а не через URL страницы.
  // Если URL нет, но sel есть — нога годная для ставки.
  if (!polyLink) return row(fork, poly, cloud, true, 'нет ссылки на ногу биржи');
  if (!cloudLink && !cloud.sel) return row(fork, poly, cloud, true, 'нет ссылки на ногу конторы');
  if (poly.asset === undefined || poly.asset.trim() === '') {
    const link = poly.betUrl || poly.url;
    const event = link ? polyEventSlug(link) : null;
    if (slugOf(link) === null && event === null) {
      return row(fork, poly, cloud, true, 'в ссылке биржи нет рынка исхода');
    }
    return row(fork, poly, cloud, true, 'токен исхода с биржи не открылся');
  }
  return row(fork, poly, cloud, true, 'смотрел бы. ставка не отправляется');
}

export function rank(rows: Paper[]): Paper[] {
  return [...rows].sort((a, b) => {
    if (a.look !== b.look) return a.look ? -1 : 1;
    let byPlus = 0;
    try {
      byPlus = new Decimal(b.roi || '0').comparedTo(new Decimal(a.roi || '0'));
    } catch {
      byPlus = 0;
    }
    if (byPlus !== 0) return byPlus;
    return a.match.localeCompare(b.match, 'ru');
  });
}
